import React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import Container from 'common/Container';
import PriceVatDisclaimer from 'common/PriceVatDisclaimer';
import { SectionHeader } from './TnoConCommercialUi';

const PLAN_ACCENT = {
  bronce: '#5C6BC0',
  plata: '#26A69A',
  oro: '#FFB300',
};

function planTierFromId(id = '') {
  const normalized = String(id).toLowerCase();
  if (normalized.includes('bronce')) return 'bronce';
  if (normalized.includes('plata')) return 'plata';
  if (normalized.includes('oro')) return 'oro';
  return null;
}

function parseInvestmentAmount(investment) {
  const match = String(investment || '').match(/[\d,.]+/);
  return match ? match[0].replace(/,/g, '') : '';
}

function currencySymbolFromInvestment(investment) {
  return String(investment || '').includes('€') ? '€' : '$';
}

function formatPlanPrice(investment) {
  const amount = parseInvestmentAmount(investment);
  if (!amount) return investment || '';
  return `${currencySymbolFromInvestment(investment)}${amount}`;
}

function planPeriodLabel(investment, plansSection) {
  if (plansSection.currencyLabel) return plansSection.currencyLabel;
  return currencySymbolFromInvestment(investment) === '€'
    ? '/ mes · EUR referencial'
    : '/ mes · USD referencial';
}

function planTagFromProduct(product) {
  if (product.tag) return product.tag;
  const match = String(product.title || '').match(/Plan\s+(Bronce|Plata|Oro)/i);
  return match ? `Plan ${match[1]}` : product.title;
}

const HorizontalPlansSection = ({ plansSection = {}, products = [], brand = {} }) => {
  const theme = useTheme();
  const primary = brand?.primary || theme.palette.primary.main;

  if (!Array.isArray(products) || products.length === 0) return null;

  const comparativa =
    plansSection.comparativa ||
    products
      .map((p) => {
        const tier = planTierFromId(p.id);
        const label = tier ? tier.charAt(0).toUpperCase() + tier.slice(1) : p.title;
        return `<strong>${label} (${p.investment || formatPlanPrice(p.investment)}):</strong> ${p.product || ''}`;
      })
      .join(' ');

  return (
    <Box sx={{ bgcolor: 'common.white' }} id="planes">
      <Container>
        <Box py={{ xs: 5, md: 8 }}>
          <SectionHeader
            overline={plansSection.overline || 'Planes de inversión'}
            title={plansSection.title || 'Bronce, Plata y Oro — mensual'}
            description={
              plansSection.description ||
              `Tres niveles de comercialización con rotativa, noticiero, Digest, inserts y — en Plata y Oro — micros en estudio o con material de marca. Inversión referencial en ${products[0] && String(products[0].investment).includes('€') ? 'EUR' : 'USD'}/mes.`
            }
            brand={brand}
          />

          <Grid container spacing={3}>
            {products.map((plan) => {
              const tier = planTierFromId(plan.id);
              const accent = (tier && PLAN_ACCENT[tier]) || primary;
              const currencySymbol = currencySymbolFromInvestment(plan.investment);
              const featureFallback = String(plan.product || '')
                .split('·')
                .map((s) => s.trim())
                .filter(Boolean);
              const features =
                Array.isArray(plan.features) && plan.features.length > 0
                  ? plan.features
                  : featureFallback;
              const highlight =
                plan.highlight != null ? plan.highlight : tier === 'plata';
              const idealFor =
                plan.idealFor ||
                (Array.isArray(plan.impacts) ? plan.impacts[plan.impacts.length - 1] : '');

              return (
                <Grid item xs={12} md={4} key={plan.id}>
                  <Box
                    component={Card}
                    height="100%"
                    display="flex"
                    flexDirection="column"
                    variant="outlined"
                    sx={{
                      borderTop: 4,
                      borderColor: accent,
                      transition: 'all .2s ease-in-out',
                      boxShadow: highlight ? 6 : 1,
                      transform: highlight ? { md: 'scale(1.02)' } : 'none',
                      '&:hover': { boxShadow: 8 },
                    }}
                  >
                    <Box
                      py={2}
                      px={2}
                      sx={{
                        bgcolor: accent,
                        color: theme.palette.common.white,
                        textAlign: 'center',
                      }}
                    >
                      <Typography variant="overline" fontWeight={700} display="block">
                        {planTagFromProduct(plan)}
                      </Typography>
                      <Typography variant="h3" fontWeight={900}>
                        {formatPlanPrice(plan.investment)}
                      </Typography>
                      <Typography variant="caption" display="block" sx={{ opacity: 0.95 }}>
                        {planPeriodLabel(plan.investment, plansSection)}
                      </Typography>
                    </Box>
                    <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                      {plan.subtitle && (
                        <Typography variant="h6" fontWeight={700} gutterBottom>
                          {plan.subtitle}
                        </Typography>
                      )}
                      {Array.isArray(plan.publicationFrequencies) && plan.publicationFrequencies.length > 0 && (
                        <Box
                          sx={{
                            mb: 2,
                            p: 1.5,
                            borderRadius: 1,
                            bgcolor: 'grey.50',
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <Typography variant="caption" color="textSecondary" fontWeight={700} display="block" sx={{ mb: 1 }}>
                            Frecuencias de publicación
                          </Typography>
                          {plan.publicationFrequencies.map((row) => (
                            <Box
                              key={row.format}
                              display="flex"
                              justifyContent="space-between"
                              alignItems="baseline"
                              gap={1}
                              sx={{ py: 0.35 }}
                            >
                              <Typography variant="body2" color="textSecondary" sx={{ flex: 1 }}>
                                {row.format}
                              </Typography>
                              <Typography variant="body2" fontWeight={700} sx={{ color: accent, whiteSpace: 'nowrap' }}>
                                {row.frequency}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      )}
                      <List dense disablePadding sx={{ flex: 1, mb: 2 }}>
                        {features.map((feature, idx) => (
                          <ListItem key={idx} disableGutters alignItems="flex-start">
                            <ListItemIcon sx={{ minWidth: 32, mt: 0.25 }}>
                              <CheckCircleIcon sx={{ color: primary }} fontSize="small" />
                            </ListItemIcon>
                            <ListItemText
                              primary={feature}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                      {plan.breakdown && typeof plan.breakdown === 'object' && (
                        <Box
                          sx={{
                            mt: 'auto',
                            pt: 2,
                            borderTop: 1,
                            borderColor: 'divider',
                          }}
                        >
                          <Typography variant="caption" color="textSecondary" display="block">
                            Desglose mensual
                          </Typography>
                          {Object.entries(plan.breakdown).map(([key, value]) =>
                            value > 0 ? (
                              <Typography variant="body2" key={key}>
                                {key}: <strong>{currencySymbol}{value}</strong>
                              </Typography>
                            ) : null
                          )}
                        </Box>
                      )}
                      {idealFor && (
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
                          {idealFor}
                        </Typography>
                      )}
                    </CardContent>
                  </Box>
                </Grid>
              );
            })}
          </Grid>

          {comparativa && (
            <Box mt={4}>
              <Box
                component={Card}
                sx={{
                  bgcolor: theme.palette.grey[100],
                  borderLeft: 4,
                  borderColor: primary,
                }}
              >
                <CardContent>
                  <Typography variant="subtitle2" sx={{ color: primary, fontWeight: 700 }} gutterBottom>
                    {plansSection.comparativaTitle || 'Comparativa rápida'}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    component="div"
                    dangerouslySetInnerHTML={{ __html: comparativa }}
                  />
                </CardContent>
              </Box>
            </Box>
          )}

          <PriceVatDisclaimer sx={{ mt: 3 }} />
        </Box>
      </Container>
    </Box>
  );
};

HorizontalPlansSection.propTypes = {
  plansSection: PropTypes.object,
  products: PropTypes.arrayOf(PropTypes.object),
  brand: PropTypes.object,
};

export default HorizontalPlansSection;
